import { gqlRequest } from './gqlClient';
import { getLeadAttribution } from '../utils/leadAttribution';
import type { LeadGuardFields } from '../hooks/useLeadGuard';

// Lead capture for trainzilla.in. Every form posts to tzilla-be, which stores
// the lead (WebLead collection), emails the team + the submitter, and shows it
// in the super-admin Website Leads inbox. Return shapes match what the form
// components already expect ({ success, error?, ... }).

export interface DemoRequest {
  id?: string;
  full_name: string;
  email: string;
  phone_number?: string;
  company_name?: string;
  business_type?: string;
  trainer_count?: string;
  preferred_time?: string;
  timezone?: string;
  specific_interests?: string[];
  message?: string;
  status?: 'requested' | 'scheduled' | 'completed' | 'cancelled';
  created_at?: string;
}

export interface ContactLead {
  full_name: string;
  email: string;
  phone_number?: string | null;
  company_name?: string | null;
  business_type?: string;
  message?: string;
  inquiry_type?: string;
  subject?: string;
  honeypot?: string;
  elapsedMs?: number;
  // Sales modal extras (optional)
  trainer_count?: string;
  current_tools?: string;
  primary_challenge?: string;
  timeline?: string;
  [extra: string]: unknown;
}

type Result = {
  success: boolean;
  error?: string;
  message?: string;
  id?: string;
  demoId?: string;
  data?: { id: string };
};

const WEB_LEAD_RESULT = '{ success id }';

/** Source + anti-spam fields added to every lead mutation (tzilla-be LeadAttributionInput). */
function leadSource(data: { honeypot?: unknown; elapsedMs?: unknown }) {
  return {
    attribution: getLeadAttribution(),
    honeypot: typeof data.honeypot === 'string' && data.honeypot ? data.honeypot : undefined,
    elapsedMs: typeof data.elapsedMs === 'number' ? Math.round(data.elapsedMs) : undefined,
  };
}

/** Drop empty values and the "Not provided" placeholder some forms send for phone. */
function clean(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'not provided') return undefined;
  return trimmed;
}

function fail(label: string, error: unknown): Result {
  console.error(`❌ ${label} failed:`, error);
  return { success: false, error: error instanceof Error ? error.message : 'Submission failed' };
}

class DatabaseService {
  /** General contact form (contact page, help-centre support form, home contact block). */
  async submitContactForm(contactData: ContactLead): Promise<Result> {
    try {
      const data = await gqlRequest<{ submitContactForm: { success: boolean; id: string } }>(
        `mutation SubmitContactForm($input: ContactFormInput!) { submitContactForm(input: $input) ${WEB_LEAD_RESULT} }`,
        {
          input: {
            ...leadSource(contactData),
            fullName: contactData.full_name,
            email: contactData.email,
            phone: clean(contactData.phone_number),
            companyName: clean(contactData.company_name),
            inquiryType: clean(contactData.inquiry_type) ?? 'general',
            subject: clean(contactData.subject) ?? 'Contact Form Inquiry',
            message: contactData.message ?? '',
          },
        },
      );
      return { success: data.submitContactForm.success, id: data.submitContactForm.id };
    } catch (error) {
      return fail('Contact form submission', error);
    }
  }

  /** "Contact sales" modal. */
  async submitContactSales(salesData: ContactLead): Promise<Result> {
    try {
      const data = await gqlRequest<{ submitSalesInquiry: { success: boolean; id: string } }>(
        `mutation SubmitSalesInquiry($input: SalesInquiryInput!) { submitSalesInquiry(input: $input) ${WEB_LEAD_RESULT} }`,
        {
          input: {
            ...leadSource(salesData),
            fullName: salesData.full_name,
            email: salesData.email,
            phone: clean(salesData.phone_number),
            companyName: clean(salesData.company_name),
            businessType: clean(salesData.business_type),
            trainerCount: clean(salesData.trainer_count),
            currentTools: clean(salesData.current_tools),
            primaryChallenge: clean(salesData.primary_challenge),
            timeline: clean(salesData.timeline),
            message: salesData.message || 'Sales inquiry',
          },
        },
      );
      return { success: data.submitSalesInquiry.success, data: { id: data.submitSalesInquiry.id } };
    } catch (error) {
      return fail('Contact sales submission', error);
    }
  }

  /** "Schedule a demo" modal. */
  async submitDemoRequest(demoData: any): Promise<Result> {
    try {
      const data = await gqlRequest<{ submitDemoRequest: { success: boolean; id: string } }>(
        `mutation SubmitDemoRequest($input: DemoRequestInput!) { submitDemoRequest(input: $input) ${WEB_LEAD_RESULT} }`,
        {
          input: {
            ...leadSource(demoData),
            fullName: demoData.full_name || demoData.name,
            email: demoData.email,
            phone: clean(demoData.phone_number || demoData.phone),
            companyName: clean(demoData.company_name),
            businessType: clean(demoData.business_type),
            trainerCount: clean(demoData.trainer_count),
            preferredTime: clean(demoData.preferred_time),
            message: clean(demoData.message || demoData.goals),
          },
        },
      );
      return { success: data.submitDemoRequest.success, demoId: data.submitDemoRequest.id };
    } catch (error) {
      return fail('Demo request submission', error);
    }
  }

  /** Video-call support callback request (Contact Support page). */
  async submitLead(leadData: any): Promise<Result> {
    try {
      const meta = leadData.metadata || {};
      const data = await gqlRequest<{ submitSupportRequest: { success: boolean; id: string } }>(
        `mutation SubmitSupportRequest($input: SupportRequestInput!) { submitSupportRequest(input: $input) ${WEB_LEAD_RESULT} }`,
        {
          input: {
            ...leadSource(leadData),
            fullName: leadData.full_name || leadData.name,
            email: leadData.email,
            phone: clean(leadData.phone_number || leadData.phone),
            message: leadData.message,
            issueCategory: clean(meta.issue_category),
            urgency: clean(meta.urgency || leadData.priority),
            preferredDate: clean(meta.preferred_date),
            preferredTime: clean(meta.preferred_time),
            languagePreference: clean(meta.language_preference),
          },
        },
      );
      return { success: data.submitSupportRequest.success, data: { id: data.submitSupportRequest.id } };
    } catch (error) {
      return fail('Lead submission', error);
    }
  }

  /** Footer / article newsletter sign-up. */
  async subscribeToNewsletter(email: string, guard: LeadGuardFields = {}): Promise<Result> {
    try {
      const data = await gqlRequest<{ subscribeNewsletter: { success: boolean; alreadySubscribed: boolean } }>(
        `mutation SubscribeNewsletter($email: String!, $attribution: LeadAttributionInput, $honeypot: String, $elapsedMs: Int) {
          subscribeNewsletter(email: $email, attribution: $attribution, honeypot: $honeypot, elapsedMs: $elapsedMs) { success alreadySubscribed }
        }`,
        { email, ...leadSource(guard) },
      );
      return data.subscribeNewsletter.alreadySubscribed
        ? { success: true, message: 'Already subscribed!' }
        : { success: true };
    } catch (error) {
      return fail('Newsletter subscription', error);
    }
  }
}

export const databaseService = new DatabaseService();
